// src/components/assessment/ChecklistTab.jsx
import React, { useMemo, useState } from "react";
import { catalog } from "../../content/catalog.v1";

export function pct(done, total) {
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

export function calcChecklistAverage(themes, phase, checkedActions) {
  const values = themes.map((t) => {
    const actions = t.levels?.[phase]?.acties || [];
    const total = actions.length;
    const done = actions.reduce((acc, a) => acc + (checkedActions?.[a.id] ? 1 : 0), 0);
    return total ? (done / total) * 100 : 0;
  });
  if (!values.length) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function ChecklistSection({
  tagline,
  actions, // [{id, text}]
  dod, // string[]
  checkedActions, // { [actionId]: boolean }
  onToggle, // (actionId, checked) => void
}) {
  const total = actions.length;
  const done = actions.reduce((acc, a) => acc + (checkedActions?.[a.id] ? 1 : 0), 0);
  const ratio = pct(done, total);
  const allDone = total > 0 && done === total;

  const accentBg = "linear-gradient(145deg, #eff6ff, #dbeafe)";
  const accentBorder = "#3b82f6";
  const accentText = "#1d4ed8";

  return (
    <div
      className="mt-3 p-3 rounded-xl"
      style={{
        background: accentBg,
        border: `1px solid ${accentBorder}40`,
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-sm font-semibold uppercase tracking-wide text-slate-600">
          Actiepunten
        </div>

        <div
          className="px-2.5 py-1 rounded-full text-xs font-semibold"
          style={{
            background: allDone
              ? "#27435d"
              : "#ffffff",
            color: allDone
              ? "#ffffff"
              : "#000000",
            boxShadow: "0 2px 6px rgba(15,23,42,0.25)",
          }}
        >
          {done}/{total} acties · {ratio}%
        </div>
      </div>

      <p className="text-xs text-slate-600 mb-2">{tagline}</p>

      <div className="space-y-2 mb-3">
        {actions.map((a) => {
          const isChecked = !!checkedActions?.[a.id];

          return (
            <div
              key={a.id}
              className="checkbox-row-3d cursor-pointer"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                padding: "10px 12px",
                borderRadius: "8px",
                cursor: "pointer",
                background: isChecked
                  ? "#27435d"
                  : "linear-gradient(145deg, #ffffff, #f9fafb)",
                border: isChecked ? `1px solid ${accentBorder}` : "1px solid #e5e7eb",
              }}
              onClick={() => onToggle(a.id, !isChecked)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle(a.id, !isChecked);
                }
              }}
            >
              <div
                className={"checkbox-box-3d mt-0.5 flex items-center justify-center rounded-md " +
                  (isChecked ? "checked" : "")}
                style={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  display: "flex",
                  borderRadius: "6px",
                  border: isChecked ? "none" : "2px solid #93c5fd",
                  background: isChecked ? "#27435d" : "#ffffff",
                }}
              >
                {isChecked && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>

              <div
                className="text-[13px] leading-snug"
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: isChecked ? "#ffffff" : "#374151",
                  textDecoration: isChecked ? "line-through" : "none",
                }}
              >
                {a.text}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="rounded-lg px-3 py-2 text-xs"
        style={{
          background: "#ffffff",
          boxShadow: "inset 0 1px 2px rgba(15,23,42,0.07)",
        }}
      >
        <div className="font-semibold mb-1" style={{ color: accentText }}>
          ✓ Definition of Done:
        </div>
        <ul className="list-disc pl-4 space-y-1">
          {dod.map((d, i) => (
            <li key={i} className="text-[11px] text-slate-700">
              {d}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Update section ──────────────────────────────────────────────────────────

function UpdateSection({ updateKey, updates, userEmail, onSaveUpdate, onDeleteUpdate }) {
  const [text, setText] = useState("");
  const [hoverDelete, setHoverDelete] = useState(null);

  const saved = useMemo(() => {
    const list = updates[updateKey] || [];
    return [...list].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [updates, updateKey]);

  function handleSave() {
    if (!text.trim()) return;
    const newUpdate = {
      timestamp: new Date().toISOString(),
      email: userEmail,
      text: text.trim(),
    };
    onSaveUpdate(updateKey, newUpdate);
    setText("");
  }

  function handleDelete(timestamp) {
    if (!window.confirm("Weet je zeker dat je deze update wilt verwijderen?")) return;
    onDeleteUpdate(updateKey, timestamp);
  }

  return (
    <div>
      <div className="text-sm font-semibold uppercase tracking-wide text-slate-600 mt-3 mb-1">
        Update
      </div>
      <textarea
        className="w-full rounded-lg p-2 border border-gray-200 text-sm"
        placeholder="Geef een update over dit thema…"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{ color: "#000000", backgroundColor: "#ffffff" }}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          style={{
            background: "#31719D",
            color: "#ffffff",
            fontSize: "11px",
            padding: "3px 10px",
            borderRadius: "6px",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
            marginTop: "4px",
          }}
        >
          Opslaan
        </button>
      </div>

      {saved.length > 0 && (
        <div className="mt-2 space-y-1">
          {saved.map((u, i) => {
            const d = new Date(u.timestamp);
            const dateStr = d.toLocaleDateString("nl-NL", {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
            const timeStr = d.toLocaleTimeString("nl-NL", {
              hour: "2-digit",
              minute: "2-digit",
            });
            const isOwn = u.email === userEmail;
            return (
              <details
                key={i}
                style={{
                  background: "#f8fafc",
                  border: "1px solid #f3f4f6",
                  borderRadius: "0.5rem",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                }}
              >
                <summary className="text-xs text-slate-600 cursor-pointer" style={{ listStyle: "none" }}>
                  <div className="flex items-center justify-between gap-2">
                    <span>▸ {dateStr}, {timeStr} — {u.email}</span>
                    {isOwn && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); handleDelete(u.timestamp); }}
                        onMouseEnter={() => setHoverDelete(i)}
                        onMouseLeave={() => setHoverDelete(null)}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: hoverDelete === i ? "#ef4444" : "#94a3b8",
                          padding: "0 2px",
                          lineHeight: 1,
                        }}
                        title="Update verwijderen"
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </summary>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{u.text}</p>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ThemeCard({ theme, phase, checkedActions, updates, userEmail, onToggle, onSaveUpdate, onSaveDashboard, onDeleteUpdate }) {
  const level = theme.levels?.[phase];
  const actions = level?.acties || [];

  const isFase1 = phase === "level1";
  const kenmerken = level?.kenmerken || [];
  const dotClass = isFase1 ? "text-emerald-500" : "text-sky-500";
  const kenmerkenLabel = isFase1 ? "Kenmerken niveau 1" : "Kenmerken niveau 2";
  const updateKey = `${theme.id}_${phase}`;

  return (
    <div className="card-3d rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center rounded-xl"
            style={{
              width: 40,
              height: 40,
              background: "linear-gradient(145deg,#eff6ff,#dbeafe)",
              boxShadow:
                "0 4px 10px rgba(37,99,235,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
            }}
          >
            <span className="text-xl">{theme.icon}</span>
          </div>
          <div>
            <div className="text-slate-900 text-xl font-extrabold leading-tight">
              {theme.title}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 mt-1">
        <div>
          <div className="section-label">{kenmerkenLabel}</div>
          <ul className="text-[12px] text-slate-700 space-y-1 mb-1">
            {kenmerken.map((k, i) => (
              <li key={i} className="flex gap-1">
                <span className={`mt-[3px] text-[10px] ${dotClass}`}>●</span>
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>

        <ChecklistSection
          phase={phase}
          title={
            isFase1
              ? "Actiepunten fase 1 – basis op orde"
              : "Actiepunten fase 2 – toepassing in de praktijk"
          }
          tagline={level?.tagline || ""}
          actions={actions}
          dod={level?.dod || []}
          checkedActions={checkedActions}
          onToggle={onToggle}
        />

        <UpdateSection
          updateKey={updateKey}
          updates={updates}
          userEmail={userEmail}
          onSaveUpdate={onSaveUpdate}
          onSaveDashboard={onSaveDashboard}
          onDeleteUpdate={onDeleteUpdate}
        />
      </div>
    </div>
  );
}

export default function ChecklistTab({ phase, checkedActions, updates, userEmail, onToggle, onSaveUpdate, onSaveDashboard, onDeleteUpdate }) {
  const isFase1 = phase === "level1";

  const overall = useMemo(
    () => calcChecklistAverage(catalog.themes, phase, checkedActions),
    [phase, checkedActions]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#ffffff" }}>
            {isFase1 ? "Fase 1 – Basis op orde" : "Fase 2 – Toepassing in praktijk"}
          </div>
          <div className="text-sm text-slate-200" >
            {isFase1
              ? "Richt je eerst op het realiseren van de basis per EA-onderwerp (niveau 1)."
              : "Als de basis staat, verschuift de focus naar toepassing in processen, gedrag en besluiten."}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            style={{
              background: "#ffffff",
              color: "#27435d",
              boxShadow: "0 3px 10px rgba(0,0,0,0.22)",
              letterSpacing: "0.01em",
            }}
          >
            <span>Gemiddelde voortgang {isFase1 ? "Fase 1" : "Fase 2"}</span>
            <span
              style={{
                background: "#31719D",
                color: "#ffffff",
                borderRadius: "999px",
                padding: "1px 9px",
                fontWeight: 800,
                fontSize: "11px",
                letterSpacing: "0.03em",
              }}
            >
              {overall.toFixed(0)}%
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {catalog.themes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            phase={phase}
            checkedActions={checkedActions}
            updates={updates}
            userEmail={userEmail}
            onToggle={onToggle}
            onSaveUpdate={onSaveUpdate}
            onSaveDashboard={onSaveDashboard}
            onDeleteUpdate={onDeleteUpdate}
          />
        ))}
      </div>
    </div>
  );
}